import { fetchApi } from '../client';

export type CheckinStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'BYPASSED';
export type SectionVisibility = 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';

export interface ConditionalOn {
  /** ID of the parent question whose answer controls this question's visibility. */
  questionId: string;
  /** The answer value that makes this question appear (e.g. "Yes", "No", or a select option). */
  value: string;
}

export interface CustomQuestion {
  id: string;
  question: string;
  /** text = free text; yesno = Yes/No toggle; select = single-choice dropdown; multiselect = multi-choice checkboxes */
  type: 'text' | 'yesno' | 'select' | 'multiselect';
  options?: string[];
  required: boolean;
  /** True when this question originated from the global standard template. */
  isDefault?: boolean;
  /**
   * When set, this question only appears if the parent question has the given answer.
   * Used for smart follow-up questions (e.g. list allergies only when "Any allergies?" = "Yes").
   */
  conditionalOn?: ConditionalOn;
  /** Age gate in months (inclusive), based on the patient's DOB. Absent means "always shown". */
  minAgeMonths?: number;
  maxAgeMonths?: number;
  /**
   * Gender gate — 'MALE'/'FEMALE' restrict the question to that gender (e.g. a pregnancy question);
   * 'ALL' or absent shows it to everyone. Matched against the gender entered in Personal Info.
   */
  forGender?: 'ALL' | 'MALE' | 'FEMALE';
}

export interface CheckinPublicView {
  checkinId: string;
  checkinToken: string;
  status: CheckinStatus;
  /** True when the backend has stored phone/DOB that must be verified before showing the form. */
  needsVerification?: boolean;
  /** Masked phone hint, e.g. "xxxxxx1234". Only present when needsVerification=true. */
  maskedPhone?: string;
  clinicName?: string;
  clinicAddress?: string;
  doctorName?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  /** True when the patient must Confirm/Reschedule/Cancel before verification/the form is shown. */
  needsConfirmation?: boolean;
  confirmationStatus?: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'RESCHEDULE_REQUESTED';
  patientName?: string;
  patientPhone?: string;
  patientEmail?: string;
  patientGender?: string;
  patientDob?: string;
  sectionPersonal: SectionVisibility;
  sectionEmergency: SectionVisibility;
  sectionComplaint: SectionVisibility;
  sectionMedicalHistory: SectionVisibility;
  sectionInsurance: SectionVisibility;
  sectionVitals: SectionVisibility;
  sectionConsent: SectionVisibility;
  customQuestions?: CustomQuestion[];
}

export interface AllergyEntry { name: string; reaction?: string; }
export interface MedicationEntry { name: string; dosage?: string; frequency?: string; }

export interface CheckinSubmitRequest {
  fullName?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  phone?: string;
  email?: string;
  preferredLanguage?: string;
  emergencyName?: string;
  emergencyPhone?: string;
  emergencyRelation?: string;
  chiefComplaint?: string;
  complaintDuration?: string;
  allergies?: AllergyEntry[];
  medications?: MedicationEntry[];
  conditions?: string[];
  surgicalHistory?: string;
  familyHistory?: string;
  insuranceProvider?: string;
  policyNumber?: string;
  groupNumber?: string;
  insuranceHolderName?: string;
  insuranceHolderDob?: string;
  heightCm?: number;
  weightKg?: number;
  bpSystolic?: number;
  bpDiastolic?: number;
  consentTreatment?: boolean;
  consentDataProcessing?: boolean;
  consentTelehealth?: boolean;
  /** multiselect responses are string[], all others are string */
  customResponses?: Record<string, string | string[]>;
  /** UI-only gate answers — ignored by the backend, persisted across step navigation */
  allergyGate?: boolean;
  medicationGate?: boolean;
  conditionGate?: boolean;
  surgeryGate?: boolean;
}

export interface CheckinStatusDto {
  checkinId: string;
  appointmentId?: string;
  status: CheckinStatus;
  linkSentAt?: string;
  linkSentVia?: string;
  expiresAt?: string;
  completedAt?: string;
  completedByStaff?: boolean;
  chiefComplaint?: string;
  complaintDuration?: string;
  tokenNumber?: number;
}

export interface CheckinInitiateRequest {
  doctorId: string;
  visitType: string;
  preferredDate: string;
  slotId?: string;
  patientId?: string;
  patientName?: string;
  patientMobile?: string;
  patientEmail?: string;
  patientAge?: number;
  patientGender?: string;
  appointmentNotes?: string;
  appointmentId?: string; // links to an already-created appointment (post-booking flow)
}

export interface CheckinSettings {
  isEnabled: boolean;
  autoTokenOnComplete: boolean;
  sendSms: boolean;
  sendEmail: boolean;
  sendWhatsapp: boolean;
  linkExpiryHours: number;
  /** Which channel to try first for OTP delivery; falls back to the other if unconfigured. */
  otpChannelPreference?: 'SMS_FIRST' | 'WHATSAPP_FIRST';
  sectionPersonal: SectionVisibility;
  sectionEmergency: SectionVisibility;
  sectionComplaint: SectionVisibility;
  sectionMedicalHistory: SectionVisibility;
  sectionInsurance: SectionVisibility;
  sectionVitals: SectionVisibility;
  sectionConsent: SectionVisibility;
  customQuestions?: CustomQuestion[];
}

// ── Pre-Visit Check-in Review & Verification (consultation) ─────────────────
export type CheckinSectionKey =
  | 'ALLERGIES' | 'CONDITIONS' | 'MEDICATIONS' | 'SURGICAL_HISTORY' | 'FAMILY_HISTORY' | 'VITALS';
export type CheckinSectionStatus = 'PENDING_REVIEW' | 'ACCEPTED' | 'SKIPPED';

export interface CheckinSectionState {
  status: CheckinSectionStatus;
  verifiedByUserId?: string;
  verifiedAt?: string;
}

export interface CheckinDetailDto {
  checkinId: string;
  appointmentId?: string;
  patientId?: string;
  status?: string;
  completedAt?: string;
  chiefComplaint?: string;
  complaintDuration?: string;
  allergies?: AllergyEntry[];
  medications?: MedicationEntry[];
  conditions?: string[];
  surgicalHistory?: string;
  familyHistory?: string;
  vitals?: { heightCm?: number; weightKg?: number; bpSystolic?: number; bpDiastolic?: number };
  consent?: { treatment: boolean; dataProcessing: boolean; telehealth: boolean; signedAt?: string };
  sections?: Record<CheckinSectionKey, CheckinSectionState>;
}

export interface CheckinSectionDecision {
  section: CheckinSectionKey;
  action: 'ACCEPTED' | 'SKIPPED';
}

export const checkinModule = {
  // ── Public (patient-facing, no auth) ────────────────────────────────────
  getPublicView: (token: string) =>
    fetchApi<CheckinPublicView>(`/api/public/checkin/${token}`),

  submit: (token: string, data: CheckinSubmitRequest) =>
    fetchApi<CheckinStatusDto>(`/api/public/checkin/${token}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPublicStatus: (token: string) =>
    fetchApi<{ status: CheckinStatus }>(`/api/public/checkin/${token}/status`),

  // ── Staff ────────────────────────────────────────────────────────────────
  initiate: (data: CheckinInitiateRequest) =>
    fetchApi<{ checkinId: string; checkinToken: string; expiresAt: string; linkSent: boolean }>(
      '/api/appointments/checkin/initiate',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  sendLink: (appointmentId: string) =>
    fetchApi<{ sent: boolean }>(`/api/appointments/${appointmentId}/checkin/send-link`, {
      method: 'POST',
    }),

  getStatus: (appointmentId: string) =>
    fetchApi<CheckinStatusDto>(`/api/appointments/${appointmentId}/checkin`),

  // ── Consultation: full check-in review + per-section verification (doctor) ──
  getDetails: (appointmentId: string) =>
    fetchApi<CheckinDetailDto>(`/api/appointments/${appointmentId}/checkin/details`),

  verify: (appointmentId: string, sections: CheckinSectionDecision[]) =>
    fetchApi<CheckinDetailDto>(`/api/appointments/${appointmentId}/checkin/verify`, {
      method: 'POST',
      body: JSON.stringify({ sections }),
    }),

  bypass: (appointmentId: string) =>
    fetchApi<{ bypassed: boolean }>(`/api/appointments/${appointmentId}/checkin/bypass`, {
      method: 'POST',
    }),

  completeOnBehalf: (appointmentId: string, data: CheckinSubmitRequest) =>
    fetchApi<CheckinStatusDto>(
      `/api/appointments/${appointmentId}/checkin/complete-onbehalf`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  // ── Walk-in / QR kiosk (public, no auth) ───────────────────────────────
  getWalkInConfig: (clinicId: string) =>
    fetchApi<CheckinPublicView & { enabled?: boolean }>(`/api/public/checkin/clinic/${clinicId}/config`),

  submitWalkIn: (clinicId: string, data: CheckinSubmitRequest) =>
    fetchApi<CheckinStatusDto>(`/api/public/checkin/clinic/${clinicId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ── Admin ────────────────────────────────────────────────────────────────
  getSettings: () =>
    fetchApi<CheckinSettings>('/api/admin/checkin/settings'),

  saveSettings: (data: Partial<CheckinSettings>) =>
    fetchApi<CheckinSettings>('/api/admin/checkin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /** Returns the global default questions template (all roles can read). */
  getDefaultQuestions: () =>
    fetchApi<{ questions: CustomQuestion[] }>('/api/admin/checkin/default-questions'),

  /** Super admin: overwrite the global default questions template. */
  updateDefaultQuestions: (questions: CustomQuestion[]) =>
    fetchApi<{ questions: CustomQuestion[]; saved: boolean }>(
      '/api/admin/checkin/default-questions',
      { method: 'PUT', body: JSON.stringify({ questions }) }
    ),

  // ── Staff: walk-in queue ─────────────────────────────────────────────────
  getWalkIns: () =>
    fetchApi<WalkInCheckin[]>('/api/checkin/walk-ins'),

  updateWalkIn: (checkinId: string, data: {
    chiefComplaint?: string;
    complaintDuration?: string;
    staffNotes?: string;
    customResponses?: Record<string, string | string[]>;
  }) =>
    fetchApi<{ updated: boolean }>('/api/checkin/walk-ins/' + checkinId, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  createTokenFromWalkIn: (checkinId: string, doctorId: string, visitType?: string) =>
    fetchApi<WalkInCheckin>('/api/checkin/walk-ins/' + checkinId + '/create-token', {
      method: 'POST',
      body: JSON.stringify({ doctorId, visitType: visitType ?? 'NEW' }),
    }),

  dismissWalkIn: (checkinId: string) =>
    fetchApi<{ dismissed: boolean }>('/api/checkin/walk-ins/' + checkinId, {
      method: 'DELETE',
    }),

  /** Staff registers a walk-in at reception without creating an appointment. */
  registerWalkIn: (data: { name?: string; phone?: string; email?: string; gender?: string; sendLink?: boolean; patientId?: string }) =>
    fetchApi<WalkInCheckin>('/api/checkin/walk-ins/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Send (or resend) the form link to a walk-in registered at the front desk. */
  sendWalkInLink: (checkinId: string) =>
    fetchApi<{ sent: boolean }>('/api/checkin/walk-ins/' + checkinId + '/send-link', {
      method: 'POST',
    }),

  /** Staff/doctor fills the full check-in form for a walk-in in person (asks the patient each question). */
  completeWalkInOnBehalf: (checkinId: string, data: CheckinSubmitRequest) =>
    fetchApi<CheckinStatusDto>('/api/checkin/walk-ins/' + checkinId + '/complete-onbehalf', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Verify patient identity (phone + DOB) before showing the check-in form. */
  verifyIdentity: (token: string, data: { phone: string; dateOfBirth?: string }) =>
    fetchApi<{ verified: boolean }>('/api/public/checkin/' + token + '/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ── Public: pre-visit confirmation gate (Confirm / Reschedule / Cancel) ──
  confirmAppointment: (token: string) =>
    fetchApi<{ confirmed: boolean }>(`/api/public/checkin/${token}/confirm`, { method: 'POST' }),

  cancelAppointment: (token: string) =>
    fetchApi<{ cancelled: boolean }>(`/api/public/checkin/${token}/cancel`, { method: 'POST' }),

  requestReschedule: (token: string) =>
    fetchApi<{ requested: boolean }>(`/api/public/checkin/${token}/request-reschedule`, { method: 'POST' }),

  // ── Public: OTP identity verification (second factor, after phone+DOB) ───
  sendOtp: (token: string, data: { phone: string; dateOfBirth?: string }) =>
    fetchApi<{ sent: boolean }>(`/api/public/checkin/${token}/send-otp`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  verifyOtp: (token: string, otp: string) =>
    fetchApi<{ verified: boolean }>(`/api/public/checkin/${token}/verify-otp`, {
      method: 'POST',
      body: JSON.stringify({ otp }),
    }),
};

export interface WalkInCheckin {
  checkinId: string;
  /** Arrival-order counter. Lower = arrived earlier = higher processing priority. */
  walkinQueueNumber?: number;
  walkinRegisteredAt?: string;
  completedAt?: string;
  /** PENDING | IN_PROGRESS | COMPLETED */
  checkinStatus?: string;
  linkSent?: boolean;
  needsVerification?: boolean;
  patientName?: string;
  phone?: string;
  email?: string;
  gender?: string;
  dateOfBirth?: string;
  address?: string;
  chiefComplaint?: string;
  complaintDuration?: string;
  surgicalHistory?: string;
  familyHistory?: string;
  allergiesJson?: string;
  medicationsJson?: string;
  conditionsJson?: string;
  customResponsesJson?: string;
  heightCm?: number;
  weightKg?: number;
  bpSystolic?: number;
  bpDiastolic?: number;
  emergencyName?: string;
  emergencyPhone?: string;
  emergencyRelation?: string;
}
